<template>
    <v-container fluid class="pa-6">
      <!-- Cabecera y Buscador -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <h1 class="text-h4 font-weight-bold mb-1">Gestión de Subcontratas</h1>
          <p class="subtitle-1 grey--text text--darken-1">Administra la lista de empresas subcontratadas del sistema</p>
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center justify-md-end">
          <v-btn
            color="primary"
            large
            rounded
            depressed
            @click="openDialog()"
          >
            <v-icon left>mdi-plus</v-icon>
            Nueva Subcontrata
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- Filtros y Tabla -->
      <v-card outlined class="rounded-xl mt-2">
        <v-card-title class="pa-4">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Buscar por nombre..."
            outlined
            dense
            hide-details
            class="max-width-300"
            clearable
          ></v-text-field>
        </v-card-title>
  
        <v-data-table
          :headers="headers"
          :items="clients"
          :search="search"
          :loading="loading"
          hover
          no-data-text="No se encontraron subcontratas"
          class="bg-transparent"
        >
          <!-- Slot para formatear el estado Activo -->
          <template v-slot:item.active="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              small
              label
              outlined
              class="font-weight-bold"
            >
              {{ item.active ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </template>
  
          <!-- Acciones -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <!-- Editar -->
              <v-btn
                icon
                small
                color="primary"
                class="mr-2"
                @click="openDialog(item)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
  
              <!-- Activar / Inactivar -->
              <v-btn
                icon
                small
                :color="item.active ? 'error' : 'success'"
                @click="toggleActive(item)"
              >
                <v-icon small>{{ item.active ? 'mdi-archive-outline' : 'mdi-archive-check-outline' }}</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- Modal para Crear/Editar -->
      <v-dialog v-model="dialog" max-width="500px" persistent>
        <v-card class="rounded-xl pa-2">
          <v-card-title class="text-h5 font-weight-bold pa-4">
              {{ isEditing ? 'Editar Subcontrata' : 'Registrar Subcontrata' }}
          </v-card-title>
  
          <v-card-text>
            <v-form ref="formRef" v-model="isFormValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="newClient.nombre"
                    label="Nombre de la empresa"
                    outlined
                    :rules="[v => !!v || 'El nombre es obligatorio']"
                    required
                  ></v-text-field>
                </v-col>
  
                <v-col cols="12">
                  <v-switch
                    v-model="newClient.active"
                    label="Subcontrata activa"
                    color="success"
                    hide-details
                    inset
                  ></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
  
          <v-divider class="mx-4"></v-divider>
  
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeDialog">Cancelar</v-btn>
            <v-btn
              color="primary"
              depressed
              width="120"
              :loading="isSaving"
              :disabled="!isFormValid"
              @click="saveClient"
            >
              {{ isEditing ? 'Actualizar' : 'Guardar' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Modal de error por nombre duplicado -->
      <v-dialog v-model="duplicateDialog" max-width="420px">
        <v-card class="pa-4 rounded-lg">
          <v-card-title class="text-h6 font-weight-bold">
            Nombre duplicado
          </v-card-title>
          <v-card-text>
            Ya existe una subcontrata con este nombre. Por favor, elige un nombre diferente.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed @click="duplicateDialog = false">
              Entendido
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Snackbar / Notificaciones -->
      <v-snackbar 
        v-model="notify.show" 
        :color="notify.color" 
        :timeout="notify.timeout"
        top
        right
      >
        <v-icon left color="white">{{ notify.icon }}</v-icon>
        {{ notify.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="notify.show = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
  import axios from 'axios';
  
  export default {
    name: 'SubcontratasMantenimiento',
    data() {
      return {
        // Sistema de notificaciones
        notify: {
          show: false,
          text: '',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000
        },
        // Estado
        search: '',
        dialog: false,
        loading: false,
        isSaving: false,
        isFormValid: false,
        duplicateDialog: false,
        isEditing: false,
        // Tabla (Vue 2 usa 'text' y 'value')
        headers: [
          { text: 'ID', value: 'id', align: 'start', sortable: true },
          { text: 'Nombre de la Subcontrata', value: 'nombre', align: 'start', sortable: true },
          { text: 'Estado', value: 'active', align: 'center' },
          { text: 'Acciones', value: 'actions', align: 'end', sortable: false },
        ],
        // Datos
        clients: [],
        // Modelo del formulario
        newClient: {
          id: null,
          nombre: '',
          active: true
        }
      };
    },
    computed:{
        idEmpresa() {
            return localStorage.getItem("clienteSeleccionado");
        }
    },
    mounted() {
      this.loadClients();
    },
    methods: {
      showNotify(text, color = 'success') {
        this.notify.text = text;
        this.notify.color = color;
        if (color === 'error') this.notify.icon = 'mdi-alert-circle';
        else if (color === 'info') this.notify.icon = 'mdi-information';
        else this.notify.icon = 'mdi-check-circle';
        this.notify.show = true;
      },
      async loadClients() {
        this.loading = true;
        try {
          const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/clientes`, {
            params: { id_empresa: this.idEmpresa }
          });
          this.clients = res.data.clientes || res.data;
        } catch (error) {
          this.showNotify('Error al cargar la lista de subcontratas', 'error');
        } finally {
          this.loading = false;
        }
      },
      existsDuplicateClient(nombre, id = null) {
        if (!nombre) return false;
        const lower = nombre.trim().toLowerCase();
        return this.clients.some(c =>
          c.nombre && c.nombre.trim().toLowerCase() === lower &&
          c.id !== id
        );
      },
      openDialog(item = null) {
        if (item && item.id !== undefined) {
          this.isEditing = true;
          this.newClient = { ...item };
        } else {
          this.isEditing = false;
          this.resetFormState();
        }
        this.dialog = true;
      },
      closeDialog() {
        this.dialog = false;
        this.resetForm();
      },
      resetFormState() {
        this.newClient = {
          id: null,
          nombre: '',
          active: true
        };
      },
      resetForm() {
        this.resetFormState();
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      },
      async saveClient() {
        if (!this.isFormValid) return;
        this.isSaving = true;
  
        try {
          if (this.existsDuplicateClient(this.newClient.nombre, this.newClient.id)) {
            this.duplicateDialog = true;
            this.isSaving = false;
            return;
          }
  
          const payload = {
            nombre: this.newClient.nombre,
            active: this.newClient.active,
            id_empresa: this.idEmpresa,
          };
  
          if (this.isEditing) {
            await axios.put(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/clientes/${this.newClient.id}`, payload);
            this.showNotify('Subcontrata actualizada correctamente', 'success');
          } else {
            await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/clientes`, payload);
            this.showNotify('Subcontrata registrada con éxito', 'success');
          }
  
          await this.loadClients();
          this.closeDialog();
        } catch (error) {
          this.showNotify('Error al procesar la solicitud', 'error');
        } finally {
          this.isSaving = false;
        }
      },
      async toggleActive(item) {
        try {
          const nuevoEstado = !item.active;
          await axios.patch(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/clientes/${item.id}/estado`, {
            active: nuevoEstado
          });
          this.showNotify(nuevoEstado ? 'Subcontrata activada' : 'Subcontrata desactivada', 'info');
          await this.loadClients();
        } catch (error) {
          console.error('Error cambiando estado:', error);
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .max-width-300 {
    max-width: 300px;
  }
  
  /* Estilos para simular el look de Vuetify 3 en los headers de la tabla */
  ::v-deep .v-data-table-header th {
    font-weight: bold !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.5px;
    color: #757575 !important;
  }
  
  .rounded-xl {
    border-radius: 16px !important;
  }
  </style>